import { IonButton, IonItem, IonLabel, IonList } from '@ionic/react';
import React, { Component } from 'react';
import _ from 'underscore';

import { Log } from '@biteinc/common';
import { TimeHelper } from '@biteinc/core-react';
import { IntegrationSystem } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import OrderHelper from '~/app/js/gcn_order_helper';
import { localizeStr } from '~/app/js/localization/localization';
import LocalStorage from '~/app/js/utils/local_storage';
import { GCNAlertView } from '~/app/js/views/gcn_alert_view';
import { LocationUtils } from '~/helpers';
import GcnGiftCardHelper from '~/helpers/gcn_gift_card_helper';
import StoredValueHelper from '~/helpers/stored_value_helper';
import { AuthService } from '~/services';

import GcnHtml from '../app/js/gcn_html';
import type { GcnTransaction, KioskLocation } from '../types';
import type { StoredValueCardWithBalance } from '../types/stored_value_card';
import type { FreedomPayEcommUtils } from './ecomm-payment/freedom-pay/freedom-pay-ecomm-form.utils';

let partialTenderTimeout: NodeJS.Timeout | null = null;

interface StoredValueProps {
  transactions: GcnTransaction[];
  submitOrder: (queuePath?: string) => void;
  location: KioskLocation;
  savedCards?: FreedomPayEcommUtils.SavedCard[];
}
export default class StoredValuePanel extends Component<StoredValueProps> {
  componentWillUnmount(): void {
    this.cancelPartialTenderTimeout();
  }

  private requiresGiftCardPaymentToken(): boolean {
    const storedValueSystem = gcn.location.getStoredValueSystem();
    const hasExternalSsoAuthUrl = !!gcn.location.getStoredValueIntegration()?.externalSsoAuthUrl;

    // Atrium on Flash only works if a payment token is passed in or external SSO auth url is set
    const requiresPaymentToken =
      (storedValueSystem === IntegrationSystem.CbordCampusCard ||
        storedValueSystem === IntegrationSystem.CbordStoredValue ||
        storedValueSystem === IntegrationSystem.AtriumStoredValue) &&
      !hasExternalSsoAuthUrl;

    return requiresPaymentToken;
  }

  private startStoredValuePayment(): void {
    const storedValueSystem = gcn.location.getStoredValueSystem();
    const sessionStoredValueCards = gcn.orderManager.getStoredValueCards().filter((card) => {
      return !!card.authSessionToken;
    });
    const requiresPaymentToken = this.requiresGiftCardPaymentToken();
    const supportsSavedCards = storedValueSystem
      ? StoredValueHelper.storedValueSupportsSavedCards(storedValueSystem)
      : false;
    const storedValueIntegration = gcn.location.getStoredValueIntegration();
    if (
      storedValueSystem &&
      !sessionStoredValueCards.length &&
      !requiresPaymentToken &&
      supportsSavedCards
    ) {
      const authUrl = storedValueIntegration?.externalSsoAuthUrl;
      if (authUrl) {
        // Use the current URL as the redirect URL
        const url = new URL(window.location.href);
        url.search = ''; // Remove query parameters
        url.searchParams.set('ssoContext', 'stored-value');
        url.hash = ''; // Remove the checkout fragment
        const currentUrl = url.toString();
        // CBORD and Atrium use the same SSO flow, but Atrium requires a payment token be passed in
        // mobileapp=3 is required to indicate that after completing SSO authentication we redirect to a url
        // the location of the redirect is indicated with redirecturl
        const loginUrl = StoredValueHelper.getStoredValueSsoUrl(
          storedValueSystem,
          authUrl,
          currentUrl,
        );

        LocalStorage.setCustomKeyItem('ssoContext', 'stored-value');
        gcn.menuView.showSpinner(localizeStr(Strings.LOADING));
        // Redirect to the SSO login page to get the payment token
        window.location.href = loginUrl;
      } else {
        AuthService.showLogin(gcn.location.attributes, () => {}, true);
      }
    } else {
      const callback = (queuePath?: string): void => {
        Log.debug('gift card used');
        // If fully paid and the gift card method allows completion of payment, proceed to next
        // step.
        if (gcn.orderManager.getGrandTotal() === 0) {
          this.props.submitOrder(queuePath);
        }
        // On first tender, we start a timeout. If they do not finish payment in 2 minutes we warn
        // them. If they don't finish 5 minutes after the warning we refund them.
        this.startPartialTenderTimeout();
      };

      if (requiresPaymentToken) {
        // Payment by token requires an email because it's generally shared with eComm.
        GcnGiftCardHelper.ensureGuestEmail(() => {
          gcn.menuView.dismissModalPopup();
          // If we previously took payment and then failed to send the order, then we're
          // already settled up
          // TODO: when did this get closed though
          if (gcn.orderManager.getGrandTotal() === 0) {
            callback();
            return;
          }
          GcnGiftCardHelper.showGiftCardView('stored-value', callback);
        });
      } else {
        GcnGiftCardHelper.showGiftCardView('stored-value', callback);
      }
    }
  }

  private getStoredValueButton(): React.ReactNode {
    if (!LocationUtils.hasStoredValueIntegration(this.props.location)) {
      return;
    }
    if (gcn.orderManager.getGrandTotal() === 0) {
      return;
    }

    const sessionStoredValueCards = gcn.orderManager.getStoredValueCards().filter((card) => {
      return !!card.authSessionToken;
    });
    const requiresPaymentToken = this.requiresGiftCardPaymentToken();
    const isStoredValueAuthed = gcn.orderManager.getIsStoredValueAuthed();

    if (sessionStoredValueCards.length && !gcn.orderManager.getUnusedStoredValueCards().length) {
      // hide button if stored value session token cards all used
      return;
    }
    // Atrium on Flash only works if a payment token is passed in.
    if (requiresPaymentToken && !sessionStoredValueCards.length) {
      return;
    }

    // If the user has already authenticated with stored value, but there are no stored value cards,
    // don't show the button
    if (isStoredValueAuthed && !sessionStoredValueCards.length) {
      return;
    }

    const storedValueButtonText = !sessionStoredValueCards.length
      ? localizeStr(Strings.LOGIN_GIFT_CARD)
      : localizeStr(Strings.USE_GIFT_CARD);

    return (
      <IonItem>
        <IonButton
          className="use-stored-value-button"
          expand="block"
          fill="outline"
          size="default"
          onClick={() => {
            this.startStoredValuePayment();
          }}
        >
          {storedValueButtonText}
        </IonButton>
      </IonItem>
    );
  }

  private static refundTransaction(transaction: GcnTransaction): void {
    gcn.menuView.showSpinner(localizeStr(Strings.REFUNDING));
    gcn.orderManager.refundStoredValueTransaction(transaction, () => {
      gcn.menuView.dismissSpinner();
    });
  }

  // If another payment is made, this will be cancelled
  private cancelPartialTenderTimeout = (): void => {
    // Check if existing timer. Starting a new one should clear an old one.
    if (partialTenderTimeout) {
      clearTimeout(partialTenderTimeout);
      partialTenderTimeout = null;
    }
  };

  private startPartialTenderTimeout = (): void => {
    this.cancelPartialTenderTimeout();
    if (gcn.orderManager.getTransactions().length === 0) {
      return;
    }
    // Pop-up will show up after 2 minutes of no additional transactions.
    partialTenderTimeout = setTimeout(() => {
      const alertText = localizeStr(Strings.PARTIAL_TENDER_TIMEOUT, [], function (string: string) {
        return string.split('\n').join('<br />');
      });
      const warningView = new GCNAlertView({
        text: alertText,
        okCallback() {
          gcn.menuView.dismissModalPopup();
        },
      });
      gcn.menuView.showModalPopup(warningView);
    }, 2 * TimeHelper.MINUTE);
  };

  private static getRemainingBalance(card: StoredValueCardWithBalance): React.ReactNode {
    if (_.isUndefined(card.remainingBalance)) {
      return undefined;
    }
    return (
      <p className="card-balance">
        {`${GcnHtml.stringFromPrice(card.remainingBalance)}`}
        {localizeStr(Strings.STORED_VALUE_REMAINING_BALANCE)}
      </p>
    );
  }

  private getTransactions(): React.ReactNode[] {
    const cardsByLastFour = gcn.orderManager
      .getStoredValueCardsAndRemainingBalance()
      .reduce(
        (
          storedValueCardsByLastFour: Record<string, StoredValueCardWithBalance>,
          card: StoredValueCardWithBalance,
        ) => {
          // only 1 card per last four
          // edge case here where we show the wrong card because last 4 is not unique
          storedValueCardsByLastFour[card.lastFour] = card;
          return storedValueCardsByLastFour;
        },
        {},
      );

    const nodes: React.ReactNode[] = [];
    if (this.props.transactions.length) {
      nodes.push(
        <IonItem className="stored-value-title">
          <IonLabel>
            <h3 className="title">{localizeStr(Strings.APPLIED_STORED_VALUE_CARDS)}</h3>
          </IonLabel>
        </IonItem>,
      );
      this.props.transactions.forEach((transaction) => {
        const card = cardsByLastFour[transaction.getLastFour()];
        const cardName = OrderHelper.getCardName(transaction);
        const cardLastFour = card?.lastFour ? ` (*${card.lastFour})` : '';
        nodes.push(
          <IonItem className="stored-value-transaction">
            <IonLabel slot="start">
              <h3 className="card-name">{`${cardName} ${cardLastFour}`}</h3>
              {StoredValuePanel.getRemainingBalance(card)}
            </IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              className="refund-stored-value-transaction"
              onClick={() => {
                StoredValuePanel.refundTransaction(transaction);
                this.startPartialTenderTimeout();
              }}
            >
              {localizeStr(Strings.REMOVE_GIFT_CARD)}
            </IonButton>
          </IonItem>,
        );
      });
    }
    return nodes;
  }

  render(): React.ReactNode {
    return (
      <IonList
        lines="none"
        className={`stored-value ${this.props.transactions.length ? 'has-transactions' : ''}`}
      >
        {this.getTransactions()}
        {this.getStoredValueButton()}
      </IonList>
    );
  }
}
